import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile — script loading and widget lifecycle.
 *
 * The site key is public (it is readable in the page source by design); it
 * lives in an env var only so a different Cloudflare widget can be pointed at
 * a staging host without a code change. .env.production carries the real one,
 * so `npm run dev` runs keyless and the widget stands down — the widget is
 * bound to videos.easylearning.school and would only ever error on localhost.
 *
 * The backend is the actual gate (it skips too when TURNSTILE_SECRET_KEY is
 * unset). Nothing here is a security boundary: a token that this module fails
 * to produce simply gets rejected server-side.
 */

export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

export const turnstileConfigured = TURNSTILE_SITE_KEY !== '';

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      appearance?: 'always' | 'execute' | 'interaction-only';
    },
  ): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/**
 * Injects the Turnstile script once and resolves when window.turnstile exists.
 * Deduped: React 18 StrictMode mounts effects twice in dev, and a second
 * <script> tag would re-run the whole runtime.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile script loaded without window.turnstile'));
    };
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      loading = null;
      reject(new Error('failed to load turnstile script'));
    };
    document.head.appendChild(script);
  });

  return loading;
}

export interface TurnstileWidget {
  /** Attach to the element the widget renders into. */
  ref: (el: HTMLDivElement | null) => void;
  /** Current token, or null when unsolved / not configured / expired. */
  token: string | null;
  /**
   * Discard the current token and re-challenge. Turnstile tokens are
   * single-use, so every failed login must call this before a retry —
   * otherwise the second attempt replays a spent token and fails as captcha
   * rather than as whatever actually went wrong.
   */
  reset: () => void;
}

/**
 * Renders one Turnstile widget into the element given the returned ref.
 *
 * A no-op (token stays null, nothing renders) when no site key is configured,
 * which is how `npm run dev` and any keyless build behave.
 */
export function useTurnstile(): TurnstileWidget {
  const [token, setToken] = useState<string | null>(null);
  const widgetId = useRef<string | null>(null);
  const el = useRef<HTMLDivElement | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    el.current = node;
  }, []);

  useEffect(() => {
    if (!turnstileConfigured || !el.current) return;
    const target = el.current;
    let cancelled = false;

    void loadTurnstile()
      .then((api) => {
        if (cancelled) return;
        widgetId.current = api.render(target, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t) => setToken(t),
          // A token can expire while the user is still typing their password.
          'expired-callback': () => setToken(null),
          // Widget failure leaves token null; submit still goes through and the
          // server decides. Better than a login page that cannot be used at all
          // because a third-party script is having a bad day.
          'error-callback': () => setToken(null),
        });
      })
      .catch(() => {
        /* script blocked or offline — same story: server decides */
      });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  return { ref, token, reset };
}
